<template>
  <el-dialog
    v-model="isVisible"
    width="600px"
    title=""
    :show-close="false"
    :close-on-click-modal="false"
    modal-class="edit-phone-number-dialog"
  >
    <template #header>
      <div class="dialog-header">
        <div class="header-content">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="white" class="header-icon">
            <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
          </svg>
          <span class="header-title">Missing Phone Number</span>
        </div>
        <button class="close-button" @click="handleClose">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" fill="white"/>
          </svg>
        </button>
      </div>
    </template>

    <div v-loading="loading" class="dialog-content">
      <p class="description-text">
        Phone number is not set for this patient. Please type the patient's number below and save to use this channel, or choose another channel.
      </p>

      <div class="input-section">
        <InfoContainer
          :is-error="showErrors && !formData.phone"
          :alert-text="showErrors && !formData.phone ? 'Required fields cannot be empty.' : ''"
          :class="{ 'info-container-with-margin': showErrors && !formData.phone }"
        >
          <div class="field-label text-[#4a4a4a] font-['Lato'] font-bold mb-2">
            Phone Number <span class="text-[#D10000]">(Required)</span>
          </div>
          <div class="flex gap-2 items-center">
            <div class="w-32 flex-none">
              <SingleSelector
                v-model="areaCodeInput"
                :options="localAreaCountryOptions"
                valueField="compositeValue"
                :prefix="flagIconURL"
                :placeholder="getAreaCodePlaceholder(areaCodeInput)"
                filterable
                :filter-method="debouncedFilterAreaCodeOptions"
                @change="handleAreaCodeChange"
              >
                <template #option-left="{ item }">
                  <span style="float: left">{{ item.countryName }}</span>
                </template>
                <template #option-right="{ item }">
                  <span style="float: right">{{ '+' + areaCodeMap[item.countryNameTwoBytes] }}</span>
                </template>
              </SingleSelector>
            </div>
            <div class="flex-1 -mt-2 h-[40px]">
              <Input
                v-model="formData.phone"
                placeholder="Phone number"
              />
            </div>
          </div>
        </InfoContainer>
      </div>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <div class="footer-divider"></div>
        <div class="footer-actions">
          <Button color-type="red-stroke" @click="handleClose">Cancel</Button>
          <Button
            :loading="saveLoading"
            :disabled="!isFormValid"
            color-type="dark-blue"
            @click="handleSave"
          >
            <span class="text-[14px] leading-5 tracking-[0.1px]" style="font-family: 'IBM Plex Sans Hebrew', sans-serif;">Update</span>
          </Button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import Input from '@/NewLibrary/Input.vue';
import Button from '@/NewLibrary/Button.vue';
import SingleSelector from '@/NewLibrary/SingleSelector.vue';
import InfoContainer from '@/NewLibrary/InfoContainer.vue';
import { getPatientByIdApi, updatePatientInfoApi } from '@/api/patient';
import { changeFlagIcon } from '@/utils/phoneUtils';
import _ from 'lodash';
import { notify } from '@/utils/notify';
const areaCodes = require('@/utils/phoneAreaCode/areaCodes.json');

export default {
  name: 'EditPhoneNumberDialog',
  components: {
    Input,
    Button,
    SingleSelector,
    InfoContainer
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    patientId: {
      type: [String, Number],
      required: true
    }
  },
  emits: ['update:visible', 'saved'],
  data() {
    return {
      isVisible: this.visible,
      loading: false,
      saveLoading: false,
      showErrors: false,
      patientData: null,
      formData: {
        phone: ''
      },
      areaCodeInput: '+1/US',
      flagIconURL: changeFlagIcon('us'),
      areaCodeMap: areaCodes,
      localAreaCountryOptions: [],
      debouncedFilterAreaCodeOptions: null
    };
  },
  computed: {
    isFormValid() {
      return this.formData.phone && this.formData.phone.trim().length > 0;
    }
  },
  watch: {
    visible(newVal) {
      this.isVisible = newVal;
      if (newVal) {
        this.fetchPatientData();
      } else {
        this.resetForm();
      }
    },
    isVisible(newVal) {
      this.$emit('update:visible', newVal);
    }
  },
  mounted() {
    this.initializeAreaCodeOptions();
    this.debouncedFilterAreaCodeOptions = _.debounce(this.filterAreaCodeOptions, 300);
  },
  methods: {
    initializeAreaCodeOptions() {
      const countryOptions = [];
      for (const [countryCode, areaCode] of Object.entries(this.areaCodeMap)) {
        countryOptions.push({
          countryNameTwoBytes: countryCode,
          countryName: countryCode,
          compositeValue: `+${areaCode}/${countryCode}`,
          areaCode: areaCode
        });
      }
      this.localAreaCountryOptions = countryOptions;
    },

    filterAreaCodeOptions(input) {
      if (!input) {
        this.initializeAreaCodeOptions();
        return;
      }
      const filtered = [];
      for (const [countryCode, areaCode] of Object.entries(this.areaCodeMap)) {
        if (countryCode.toLowerCase().includes(input.toLowerCase()) ||
            areaCode.includes(input)) {
          filtered.push({
            countryNameTwoBytes: countryCode,
            countryName: countryCode,
            compositeValue: `+${areaCode}/${countryCode}`,
            areaCode: areaCode
          });
        }
      }
      this.localAreaCountryOptions = filtered;
    },

    getAreaCodePlaceholder(value) {
      if (!value) return '+1';
      const parts = value.split('/');
      return parts[0] || '+1';
    },

    handleAreaCodeChange(value) {
      // Extract country code from the composite value (format: "+1/US")
      if (value && value.includes('/')) {
        const countryCode = value.split('/')[1];
        this.flagIconURL = changeFlagIcon(countryCode.toLowerCase());
      }
    },

    async fetchPatientData() {
      this.loading = true;
      try {
        const response = await getPatientByIdApi(this.patientId);
        this.patientData = response.data;

        // Extract phone from patient_contact array
        const phoneContact = this.patientData.patient_contact?.find(c => c.contact_type === 'phone');
        const phone = phoneContact?.contact_details?.trim() || '';

        // Extract phone country code if present in format "+1-1111111111"
        if (phone && phone.includes('+')) {
          const phoneMatch = phone.match(/^\+(\d+)/);
          if (phoneMatch) {
            const areaCode = phoneMatch[1];
            let countryCode = null;

            // For +1 area code, prioritize US
            if (areaCode === '1' && this.areaCodeMap['US'] === '1') {
              countryCode = 'US';
            } else {
              // Find matching country code
              for (const [cc, code] of Object.entries(this.areaCodeMap)) {
                if (code === areaCode) {
                  countryCode = cc;
                  break;
                }
              }
            }

            if (countryCode) {
              this.areaCodeInput = `+${areaCode}/${countryCode}`;
              this.flagIconURL = changeFlagIcon(countryCode.toLowerCase());
              // Remove area code from phone display
              this.formData.phone = phone.replace(/^\+\d+-?/, '');
            }
          }
        } else {
          this.formData.phone = phone;
        }
      } catch (error) {
        console.error('Error fetching patient data:', error);
        notify({ type: 'error', message: 'Failed to load patient data' });
      } finally {
        this.loading = false;
      }
    },

    async handleSave() {
      if (!this.isFormValid) {
        this.showErrors = true;
        return;
      }

      this.saveLoading = true;

      try {
        // Format phone with area code prefix (e.g., "+1-1111111111")
        const areaCodePrefix = this.areaCodeInput.split('/')[0]; // Extract "+1" from "+1/US"
        const formattedPhone = `${areaCodePrefix}-${this.formData.phone}`;

        const updateData = {
          patient_id: this.patientData.patient_id,
          patient_phone: formattedPhone
        };

        await updatePatientInfoApi(updateData);

        notify({ type: 'success', message: 'Phone number updated successfully' });
        this.$emit('saved');
        this.handleClose();
      } catch (error) {
        console.error('Error updating patient:', error);
        notify({ type: 'error', message: 'Failed to update phone number' });
      } finally {
        this.saveLoading = false;
      }
    },

    handleClose() {
      this.isVisible = false;
      this.$emit('update:visible', false);
      this.resetForm();
    },

    resetForm() {
      this.showErrors = false;
      this.patientData = null;
      this.formData.phone = '';
      this.areaCodeInput = '+1/US';
      this.flagIconURL = changeFlagIcon('us');
    }
  }
};
</script>

<style scoped>
.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: #004879;
  border-radius: 10px 10px 0 0;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 4px;
}

.header-icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.header-title {
  font-family: 'Inter', sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: normal;
  color: white;
}

.close-button {
  background: transparent;
  border: none;
  cursor: pointer;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.dialog-content {
  padding: 24px 32px;
  background: #f9fbfd;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.description-text {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.4px;
  color: #333;
  margin: 0;
}

.input-section {
  padding-left: 100px;
  padding-right: 100px;
}

.field-label {
  margin-bottom: 8px;
}

.info-container-with-margin {
  margin-bottom: 8px;
}

.dialog-footer {
  padding: 0 24px 24px 24px;
  background: #f9fbfd;
}

.footer-divider {
  border-top: 1px dashed #C4C4C4;
  padding-top: 24px;
}

.footer-actions {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}
</style>

<style>
.edit-phone-number-dialog .el-overlay-dialog {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.edit-phone-number-dialog .el-dialog {
  border-radius: 10px;
  margin: 0 auto;
  --el-dialog-padding-primary: 0px !important;
}

.edit-phone-number-dialog .el-dialog__header {
  padding: 0;
  margin: 0;
}

.edit-phone-number-dialog .el-dialog__body {
  padding: 0;
}

.edit-phone-number-dialog .el-dialog__footer {
  padding: 0;
}

.edit-phone-number-dialog .new-libary-single-selector {
  height: 40px;
}

.edit-phone-number-dialog .new-libary-single-selector .el-select__wrapper {
  border: 1px solid #C4C4C4 !important;
  box-shadow: none !important;
  height: 40px !important;
  min-height: 40px !important;
}

.edit-phone-number-dialog .new-libary-single-selector .el-select__wrapper:hover,
.edit-phone-number-dialog .new-libary-single-selector .el-select__wrapper:focus,
.edit-phone-number-dialog .new-libary-single-selector .el-select__wrapper.is-focus {
  border: 1px solid #C4C4C4 !important;
  box-shadow: none !important;
  height: 40px !important;
}
</style>
